import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../utils/logger';
import {
  Shield,
  Users,
  UserPlus,
  Search,
  Edit,
  Trash2,
  Check,
  X,
  ChevronDown,
  Target,
  Lock,
  AlertTriangle,
  Crown,
  UserCog,
  Layout,
  BarChart3,
  Activity,
  Eye,
  Clock,
  LogIn,
  LogOut,
  Monitor,
  MousePointer,
  Timer,
  RefreshCw,
  Wifi,
  WifiOff
} from 'lucide-react';
import { useRBAC } from '../contexts/RBACContext';
import { useData } from '../contexts/DataContext';
import { useActivityTracking } from '../contexts/ActivityTrackingContext';
import { getRoleDisplayName, getRoleBadgeColor, ROLES, ALL_PAGES, DEFAULT_ROLE_PAGES, isDeveloperEmail, PERMISSIONS } from '../config/rbac';
import { MessageSquare } from 'lucide-react';
import ActivityReports from '../components/admin/ActivityReports';

const AdminPanel = () => {
  const {
    userRole,
    allUsers,
    fetchAllUsers,
    updateUserRole,
    updateUserCampaigns,
    updateUserPages,
    updateUserPermissions,
    addUser,
    removeUser,
    isAdminOrHigher,
    isDeveloper,
    canManageUser,
    startImpersonation,
    canImpersonate,
    isImpersonating
  } = useRBAC();

  const { leads } = useData();
  
  // Activity tracking context
  const activityTracking = useActivityTracking();
  const { 
    getDetailedUserActivity, 
    trackedUsers, 
    fetchTrackedUsers,
    canViewTracking 
  } = activityTracking || {};

  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState(ROLES.MODERATOR);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [selectedPages, setSelectedPages] = useState(DEFAULT_ROLE_PAGES[ROLES.MODERATOR] || []);
  const [showPermissions, setShowPermissions] = useState(null);
  const [showPagePermissions, setShowPagePermissions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'activity'
  
  // User activity modal state
  const [showUserActivity, setShowUserActivity] = useState(false);
  const [selectedUserActivity, setSelectedUserActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);

  // Get unique campaigns from leads
  const availableCampaigns = useMemo(() => {
    const campaigns = [...new Set(leads.map(l => l.campaign_name).filter(Boolean))];
    return campaigns.sort();
  }, [leads]);

  // Load users on mount
  useEffect(() => {
    if (isAdminOrHigher()) {
      fetchAllUsers();
      // Also fetch tracked users for activity data
      if (fetchTrackedUsers) {
        fetchTrackedUsers();
      }
    }
  }, [fetchAllUsers, isAdminOrHigher, fetchTrackedUsers]);

  // Handle viewing user activity
  const handleViewUserActivity = async (userEmail) => {
    if (!getDetailedUserActivity) return;
    
    setLoadingActivity(true);
    setShowUserActivity(true);
    
    try {
      const activityData = await getDetailedUserActivity(userEmail);
      setSelectedUserActivity(activityData);
    } catch (err) {
      logger.error('Error fetching user activity:', err);
    } finally {
      setLoadingActivity(false);
    }
  };

  // Format duration helper
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    // If less than 1 minute ago
    if (diff < 60000) return 'Just now';
    // If less than 1 hour ago
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    // If less than 24 hours ago
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    // Otherwise show date
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get user presence status from trackedUsers
  const getUserPresence = (email) => {
    return trackedUsers?.find(u => u.email === email);
  };

  // Filter users
  const filteredUsers = useMemo(() => {
    return allUsers.filter(user => {
      const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [allUsers, searchTerm, roleFilter]);

  // Handle add user
  const handleAddUser = async () => {
    if (!newUserEmail.trim()) {
      setError('Email is required');
      return;
    }

    if (isDeveloperEmail(newUserEmail)) {
      setError('Cannot add developer email through this form');
      return;
    }

    if (newUserRole !== ROLES.ADMIN && selectedCampaigns.length === 0) {
      setError('Please assign at least one campaign for this user');
      return;
    }

    if (newUserRole !== ROLES.ADMIN && selectedPages.length === 0) {
      setError('Please select at least one page access for this user');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const pagesToAssign = newUserRole === ROLES.ADMIN 
        ? ALL_PAGES.map(p => p.key) 
        : selectedPages;
      
      await addUser(newUserEmail.trim(), newUserRole, selectedCampaigns, pagesToAssign);
      setSuccess(`User ${newUserEmail} added successfully`);
      setShowAddUser(false);
      setNewUserEmail('');
      setNewUserRole(ROLES.MODERATOR);
      setSelectedCampaigns([]);
      setSelectedPages(DEFAULT_ROLE_PAGES[ROLES.MODERATOR] || []);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle role change
  const handleRoleChange = async (email, newRole) => {
    setLoading(true);
    setError('');

    try {
      await updateUserRole(email, newRole);
      setSuccess(`Role updated for ${email}`);
      setEditingUser(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle campaign assignment
  const handleCampaignChange = async (email, campaigns) => {
    setLoading(true);
    setError('');

    try {
      await updateUserCampaigns(email, campaigns);
      setSuccess(`Campaigns updated for ${email}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Toggle campaign selection
  const toggleCampaign = (campaign, currentCampaigns, email) => {
    const newCampaigns = currentCampaigns.includes(campaign)
      ? currentCampaigns.filter(c => c !== campaign)
      : [...currentCampaigns, campaign];
    handleCampaignChange(email, newCampaigns);
  };

  // Handle page access change
  const handlePageChange = async (email, pages) => {
    setLoading(true);
    setError('');

    try {
      await updateUserPages(email, pages);
      setSuccess(`Page access updated for ${email}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Toggle page selection
  const togglePage = (page, currentPages, email) => {
    const newPages = currentPages?.includes(page)
      ? currentPages.filter(p => p !== page)
      : [...(currentPages || []), page];
    handlePageChange(email, newPages);
  };

  // Toggle chatbot access for a user
  const toggleChatbotAccess = async (email, currentPermissions) => {
    setLoading(true);
    setError('');
    try {
      const permissions = currentPermissions || [];
      const hasChatbot = permissions.includes(PERMISSIONS.ACCESS_CHATBOT);
      const newPermissions = hasChatbot
        ? permissions.filter(p => p !== PERMISSIONS.ACCESS_CHATBOT)
        : [...permissions, PERMISSIONS.ACCESS_CHATBOT];
      
      await updateUserPermissions(email, newPermissions);
      setSuccess(`Chatbot access ${hasChatbot ? 'disabled' : 'enabled'} for ${email}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Check if user has chatbot access
  const hasChatbotAccess = (user) => {
    if (user.role === ROLES.DEVELOPER || user.role === ROLES.ADMIN) return true;
    return user.custom_permissions?.includes(PERMISSIONS.ACCESS_CHATBOT) || false;
  };

  // Toggle delete access for a user
  const toggleDeleteAccess = async (email, currentPermissions) => {
    setLoading(true);
    setError('');
    try {
      const permissions = currentPermissions || [];
      const hasDelete = permissions.includes(PERMISSIONS.CAN_DELETE);
      const newPermissions = hasDelete
        ? permissions.filter(p => p !== PERMISSIONS.CAN_DELETE)
        : [...permissions, PERMISSIONS.CAN_DELETE];
      
      await updateUserPermissions(email, newPermissions);
      setSuccess(`Delete access ${hasDelete ? 'disabled' : 'enabled'} for ${email}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Check if user has delete access
  const hasDeleteAccess = (user) => {
    if (user.role === ROLES.DEVELOPER || user.role === ROLES.ADMIN) return true;
    return user.custom_permissions?.includes(PERMISSIONS.CAN_DELETE) || false;
  };

  // Handle remove user
  const handleRemoveUser = async (email) => {
    if (!window.confirm(`Are you sure you want to remove ${email}?`)) return;
    
    setLoading(true);
    setError('');

    try {
      await removeUser(email);
      setSuccess(`User ${email} removed successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update selectedPages when role changes
  const handleNewUserRoleChange = (role) => {
    setNewUserRole(role);
    setSelectedPages(DEFAULT_ROLE_PAGES[role] || []);
    if (role === ROLES.ADMIN) {
      setSelectedCampaigns([]);
    }
  };

  // Check access
  if (!isAdminOrHigher()) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
          <p className="text-gray-500 mt-2">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 px-2">
      {/* Header */}
      <div className="mb-6 bg-white rounded-2xl p-6 shadow-sm border border-blue-100">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
              <p className="text-gray-500">Manage users, roles, access control & activity tracking</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3 px-4 py-2 bg-blue-50 rounded-xl border border-blue-100">
            <span className="text-sm text-gray-600">Logged in as:</span>
            <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${getRoleBadgeColor(userRole)}`}>
              {getRoleDisplayName(userRole)}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center space-x-2 px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px ${
              activeTab === 'users'
                ? 'text-blue-600 border-blue-600'
                : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>User Management</span>
          </button>
          {isAdminOrHigher() && (
            <button
              onClick={() => setActiveTab('activity')}
              className={`flex items-center space-x-2 px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px ${
                activeTab === 'activity'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Activity className="w-4 h-4" />
              <span>User Activity Tracking</span>
              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-600 rounded-full font-semibold">NEW</span>
            </button>
          )}
        </div>
      </div>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2"
          >
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
            <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2"
          >
            <Check className="w-5 h-5 text-green-500" />
            <span className="text-green-700">{success}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tab Content */}
      {activeTab === 'activity' ? (
        <ActivityReports />
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-8">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-blue-100 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Total Users</p>
              <p className="text-3xl font-bold text-gray-900">{allUsers.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-blue-100 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Admins</p>
              <p className="text-3xl font-bold text-gray-900">
                {allUsers.filter(u => u.role === ROLES.ADMIN).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Crown className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-blue-100 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Moderators</p>
              <p className="text-3xl font-bold text-gray-900">
                {allUsers.filter(u => u.role === ROLES.MODERATOR).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <UserCog className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-blue-100 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Campaigns</p>
              <p className="text-3xl font-bold text-gray-900">{availableCampaigns.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-5 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search users by email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all"
            />
          </div>

          <div className="flex items-center space-x-4">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value="all">All Roles</option>
              <option value={ROLES.ADMIN}>Admin</option>
              <option value={ROLES.MODERATOR}>Moderator</option>
              <option value={ROLES.USER}>User</option>
            </select>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowAddUser(true)}
              className="flex items-center space-x-2 px-5 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20"
            >
              <UserPlus className="w-5 h-5" />
              <span className="font-medium">Add User</span>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 overflow-hidden">
        <div className="overflow-x-auto" style={{ minHeight: '400px' }}>
          <table className="w-full">
            <thead className="bg-blue-50 border-b border-blue-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Campaigns</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Page Access</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Chatbot</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Delete</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => {
                const isPermanentDev = isDeveloperEmail(user.email);
                const canEdit = !isPermanentDev && canManageUser(user.email, user.role);

                return (
                  <tr key={user.email} className={`hover:bg-gray-50 ${isPermanentDev ? 'bg-purple-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-semibold">
                            {user.email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 flex items-center">
                            {user.email}
                            {isPermanentDev && <Lock className="w-3 h-3 ml-2 text-purple-600" />}
                          </div>
                          <div className="text-xs text-gray-500">
                            Added {new Date(user.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingUser === user.email && canEdit ? (
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.email, e.target.value)}
                          className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                          disabled={loading}
                        >
                          {isDeveloper() && <option value={ROLES.ADMIN}>Admin</option>}
                          <option value={ROLES.MODERATOR}>Moderator</option>
                          <option value={ROLES.USER}>User</option>
                        </select>
                      ) : (
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getRoleBadgeColor(user.role)}`}>
                          {getRoleDisplayName(user.role)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {isPermanentDev ? (
                        <span className="text-sm text-purple-600 font-medium">All Campaigns</span>
                      ) : user.role === ROLES.ADMIN ? (
                        <span className="text-sm text-gray-500">All Campaigns</span>
                      ) : (
                        <div className="relative">
                          <button
                            onClick={() => setShowPermissions(showPermissions === user.email ? null : user.email)}
                            className="flex items-center space-x-1 text-sm text-gray-700 hover:text-blue-600"
                            disabled={!canEdit}
                          >
                            <span>{user.assigned_campaigns?.length || 0} campaign(s)</span>
                            <ChevronDown className="w-4 h-4" />
                          </button>

                          {showPermissions === user.email && canEdit && (
                            <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl p-2 max-h-48 overflow-y-auto">
                              {availableCampaigns.length === 0 ? (
                                <p className="text-sm text-gray-500 p-2">No campaigns available</p>
                              ) : (
                                availableCampaigns.map((campaign) => (
                                  <label key={campaign} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={user.assigned_campaigns?.includes(campaign)}
                                      onChange={() => toggleCampaign(campaign, user.assigned_campaigns || [], user.email)}
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">{campaign}</span>
                                  </label>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {isPermanentDev ? (
                        <span className="text-sm text-purple-600 font-medium">All Pages</span>
                      ) : user.role === ROLES.ADMIN ? (
                        <span className="text-sm text-gray-500">All Pages</span>
                      ) : (
                        <div className="relative">
                          <button
                            onClick={() => setShowPagePermissions(showPagePermissions === user.email ? null : user.email)}
                            className="flex items-center space-x-1 text-sm text-gray-700 hover:text-blue-600"
                            disabled={!canEdit}
                          >
                            <Layout className="w-4 h-4 mr-1" />
                            <span>{user.allowed_pages?.length || 0} page(s)</span>
                            <ChevronDown className="w-4 h-4" />
                          </button>

                          {showPagePermissions === user.email && canEdit && (
                            <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl p-2 max-h-64 overflow-y-auto">
                              {ALL_PAGES.map((page) => (
                                <label key={page.key} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={user.allowed_pages?.includes(page.key)}
                                    onChange={() => togglePage(page.key, user.allowed_pages || [], user.email)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <div>
                                    <span className="text-sm text-gray-700 font-medium">{page.name}</span>
                                    <p className="text-xs text-gray-400">{page.description}</p>
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {/* Chatbot Access Toggle */}
                      {isPermanentDev ? (
                        <span className="flex items-center text-sm text-purple-600 font-medium">
                          <MessageSquare className="w-4 h-4 mr-1" />
                          Always On
                        </span>
                      ) : user.role === ROLES.ADMIN ? (
                        <span className="flex items-center text-sm text-gray-500">
                          <MessageSquare className="w-4 h-4 mr-1" />
                          Always On
                        </span>
                      ) : (
                        <button
                          onClick={() => toggleChatbotAccess(user.email, user.custom_permissions)}
                          disabled={!canEdit || loading}
                          className={`flex items-center space-x-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                            hasChatbotAccess(user)
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          } ${!canEdit ? 'cursor-not-allowed opacity-50' : ''}`}
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                          <span>{hasChatbotAccess(user) ? 'Enabled' : 'Disabled'}</span>
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {/* Delete Access Toggle */}
                      {isPermanentDev ? (
                        <span className="flex items-center text-sm text-purple-600 font-medium">
                          <Trash2 className="w-4 h-4 mr-1" />
                          Always On
                        </span>
                      ) : user.role === ROLES.ADMIN ? (
                        <span className="flex items-center text-sm text-gray-500">
                          <Trash2 className="w-4 h-4 mr-1" />
                          Always On
                        </span>
                      ) : (
                        <button
                          onClick={() => toggleDeleteAccess(user.email, user.custom_permissions)}
                          disabled={!canEdit || loading}
                          className={`flex items-center space-x-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                            hasDeleteAccess(user)
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          } ${!canEdit ? 'cursor-not-allowed opacity-50' : ''}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>{hasDeleteAccess(user) ? 'Enabled' : 'Disabled'}</span>
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {/* Status - shows online/offline for tracked users (moderators only) */}
                      {user.role === ROLES.MODERATOR && canViewTracking?.() ? (
                        (() => {
                          const presence = getUserPresence(user.email);
                          const isOnline = presence?.status === 'online';
                          return (
                            <div className="flex items-center space-x-2">
                              <span className={`flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                                isOnline ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {isOnline ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
                                {isOnline ? 'Online' : 'Offline'}
                              </span>
                              {presence?.lastSeen && !isOnline && (
                                <span className="text-xs text-gray-400">{formatDate(presence.lastSeen)}</span>
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          user.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-2">
                        {/* View as User button - only for non-developer users when admin can impersonate */}
                        {!isPermanentDev && canImpersonate() && !isImpersonating && (
                          <button
                            onClick={() => startImpersonation(user)}
                            className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded flex items-center"
                            title={`View as ${user.email}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        {/* View Activity button for moderators */}
                        {user.role === ROLES.MODERATOR && canViewTracking?.() && (
                          <button
                            onClick={() => handleViewUserActivity(user.email)}
                            className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded flex items-center"
                            title="View activity"
                          >
                            <Activity className="w-4 h-4" />
                          </button>
                        )}
                        {isPermanentDev ? (
                          <span className="text-xs text-purple-600">Protected</span>
                        ) : canEdit ? (
                          <>
                            <button
                              onClick={() => setEditingUser(editingUser === user.email ? null : user.email)}
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded"
                              title="Edit role"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRemoveUser(user.email)}
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Remove user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">No access</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No users found</p>
            </div>
          )}
        </div>
      </div>

      {/* Add User Modal */}
      <AnimatePresence>
        {showAddUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddUser(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Add New User</h3>
                <button onClick={() => setShowAddUser(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="user@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={newUserRole}
                    onChange={(e) => handleNewUserRoleChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {isDeveloper() && <option value={ROLES.ADMIN}>Admin (Full Access)</option>}
                    <option value={ROLES.MODERATOR}>Moderator (Limited Access)</option>
                    <option value={ROLES.USER}>User (Basic Access)</option>
                  </select>
                </div>

                {newUserRole !== ROLES.ADMIN && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Target className="w-4 h-4 inline mr-1" />
                        Assign Campaigns <span className="text-red-500">*</span>
                      </label>
                      <div className="border border-gray-300 rounded-lg p-2 max-h-32 overflow-y-auto">
                        {availableCampaigns.length === 0 ? (
                          <p className="text-sm text-gray-500 p-2">No campaigns available</p>
                        ) : (
                          availableCampaigns.map((campaign) => (
                            <label key={campaign} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedCampaigns.includes(campaign)}
                                onChange={() => {
                                  setSelectedCampaigns(prev =>
                                    prev.includes(campaign) ? prev.filter(c => c !== campaign) : [...prev, campaign]
                                  );
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">{campaign}</span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Layout className="w-4 h-4 inline mr-1" />
                        Page Access <span className="text-red-500">*</span>
                      </label>
                      <div className="border border-gray-300 rounded-lg p-2 max-h-40 overflow-y-auto">
                        {ALL_PAGES.map((page) => (
                          <label key={page.key} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedPages.includes(page.key)}
                              onChange={() => {
                                setSelectedPages(prev =>
                                  prev.includes(page.key) ? prev.filter(p => p !== page.key) : [...prev, page.key]
                                );
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div>
                              <span className="text-sm text-gray-700 font-medium">{page.name}</span>
                              <p className="text-xs text-gray-400">{page.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setShowAddUser(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddUser}
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Adding...' : 'Add User'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* User Activity Modal */}
      <AnimatePresence>
        {showUserActivity && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowUserActivity(false);
              setSelectedUserActivity(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-white text-xl font-bold">
                      {selectedUserActivity?.email?.charAt(0).toUpperCase() || '?'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{selectedUserActivity?.email || 'Loading...'}</h3>
                    <div className="flex items-center space-x-2 mt-1">
                      {selectedUserActivity?.status === 'online' ? (
                        <span className="flex items-center text-sm text-green-600 font-medium">
                          <Wifi className="w-4 h-4 mr-1" />
                          Online
                          {selectedUserActivity?.currentPage && (
                            <span className="ml-2 text-gray-500">
                              on <span className="capitalize">{selectedUserActivity.currentPage}</span>
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="flex items-center text-sm text-gray-500">
                          <WifiOff className="w-4 h-4 mr-1" />
                          Offline
                          {selectedUserActivity?.lastSeen && (
                            <span className="ml-2">· Last seen {formatDate(selectedUserActivity.lastSeen)}</span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowUserActivity(false);
                    setSelectedUserActivity(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                {loadingActivity ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                  </div>
                ) : selectedUserActivity ? (
                  <div className="space-y-6">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-blue-50 rounded-xl p-4 text-center">
                        <LogIn className="w-5 h-5 text-blue-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-blue-600">{selectedUserActivity.totalLogins || 0}</p>
                        <p className="text-xs text-gray-600">Total Logins</p>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-4 text-center">
                        <Monitor className="w-5 h-5 text-purple-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-purple-600">{selectedUserActivity.totalPageVisits || 0}</p>
                        <p className="text-xs text-gray-600">Page Visits</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-4 text-center">
                        <Timer className="w-5 h-5 text-green-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-green-600">{formatDuration(selectedUserActivity.totalTimeSpentSeconds)}</p>
                        <p className="text-xs text-gray-600">Total Time</p>
                      </div>
                      <div className="bg-orange-50 rounded-xl p-4 text-center">
                        <MousePointer className="w-5 h-5 text-orange-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-orange-600">{selectedUserActivity.totalButtonClicks || 0}</p>
                        <p className="text-xs text-gray-600">Button Clicks</p>
                      </div>
                    </div>

                    {/* Current Activity (if online) */}
                    {selectedUserActivity.status === 'online' && selectedUserActivity.currentAction && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-green-800 mb-2 flex items-center">
                          <Activity className="w-4 h-4 mr-2" />
                          Currently Doing
                        </h4>
                        <p className="text-green-700 font-medium">{selectedUserActivity.currentAction}</p>
                        {selectedUserActivity.currentPage && (
                          <p className="text-sm text-green-600 mt-1">
                            On page: <span className="capitalize font-medium">{selectedUserActivity.currentPage}</span>
                          </p>
                        )}
                      </div>
                    )}

                    {/* Login/Activity Info */}
                    <div className="bg-gray-50 rounded-xl p-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        Activity Timeline
                      </h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Registered At</p>
                          <p className="font-medium text-gray-900">{formatDate(selectedUserActivity.registeredAt)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Last Login</p>
                          <p className="font-medium text-gray-900">{formatDate(selectedUserActivity.lastLogin)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Last Logout</p>
                          <p className="font-medium text-gray-900">{formatDate(selectedUserActivity.lastLogout)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Total Activities</p>
                          <p className="font-medium text-gray-900">{selectedUserActivity.totalActivities || 0}</p>
                        </div>
                      </div>
                    </div>

                    {/* Most Visited Pages */}
                    {selectedUserActivity.mostVisitedPages?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                          <BarChart3 className="w-4 h-4 mr-2" />
                          Most Visited Pages
                        </h4>
                        <div className="space-y-2">
                          {selectedUserActivity.mostVisitedPages.map((page, idx) => (
                            <div key={page.page} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                              <div className="flex items-center space-x-3">
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                  idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                                  idx === 1 ? 'bg-gray-200 text-gray-700' :
                                  idx === 2 ? 'bg-orange-100 text-orange-700' :
                                  'bg-blue-50 text-blue-600'
                                }`}>
                                  {idx + 1}
                                </span>
                                <span className="font-medium text-gray-700 capitalize">{page.page}</span>
                              </div>
                              <div className="flex items-center space-x-4 text-sm">
                                <span className="text-gray-500">{page.visits} visits</span>
                                <span className="text-blue-600 font-medium">{formatDuration(page.timeSpent)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Time Spent Per Page */}
                    {selectedUserActivity.timePerPage && Object.keys(selectedUserActivity.timePerPage).length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                          <Timer className="w-4 h-4 mr-2" />
                          Time Spent Per Page
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {Object.entries(selectedUserActivity.timePerPage)
                            .sort((a, b) => b[1] - a[1])
                            .slice(0, 6)
                            .map(([page, seconds]) => (
                              <div key={page} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                <span className="text-sm text-gray-700 capitalize">{page}</span>
                                <span className="text-sm font-medium text-blue-600">{formatDuration(seconds)}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Recent Activities */}
                    {selectedUserActivity.recentActivities?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                          <Activity className="w-4 h-4 mr-2" />
                          Recent Activities
                        </h4>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {selectedUserActivity.recentActivities.slice(0, 15).map((activity, idx) => (
                            <div key={idx} className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded-lg">
                              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                                activity.type === 'login' ? 'bg-green-500' :
                                activity.type === 'logout' ? 'bg-red-500' :
                                activity.type === 'page_visit' ? 'bg-blue-500' :
                                activity.type === 'button_click' ? 'bg-purple-500' :
                                activity.type === 'feature_usage' ? 'bg-orange-500' :
                                'bg-gray-400'
                              }`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-900 truncate">{activity.description}</p>
                                <p className="text-xs text-gray-500">
                                  {formatDate(activity.timestamp)}
                                  {activity.page && <span className="ml-2">· <span className="capitalize">{activity.page}</span></span>}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Activity className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No activity data available</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
        </>
      )}
    </div>
  );
};

export default AdminPanel;
